import React from 'react';

import { AuthLayout } from '../auth/AuthLayout';
import { NotificationProvider } from '../contexts/NotificationContext';
import { PermissionCenterProvider } from '../contexts/PermissionCenterContext';
import { DarkModeProvider } from '../theme/DarkModeProvider';

const requireProp = (value, name) => {
  if (!value) throw new Error(`AppShell: ${name} is required`);
};

/**
 * The root of a Deacon app: theme, notifications, the permission center and
 * the authenticated layout, in the order they depend on each other.
 *
 * PermissionCenterProvider sits inside NotificationProvider because a role
 * request reports its outcome as a toast, and it mounts PermissionRequestProvider
 * itself, so apps must not mount another one. Apps that use MUI date pickers
 * wrap their own content in a LocalizationProvider (x-date-pickers is not a core
 * peer dependency).
 *
 * @param {Object} props
 * @param {string} props.appName - App slug: dark-mode storage key and permission-center app name.
 * @param {string} props.title - Title shown in the app bar.
 * @param {Object} props.api - The app's `createAppTransport` client.
 * @param {string} [props.permissionPrefix=appName] - Permission id prefix, when it differs from appName.
 * @param {Object} [props.themeOptions] - Extra MUI theme options. Keep the object at module
 *   scope: the theme is memoised on its identity.
 * @param {Object} [props.contentSx] - sx for the content area (AuthLayout's default when omitted).
 * @param {React.ReactNode} [props.headerContent] - Extra app-bar content.
 * @param {React.ReactNode | function({ userInfo, isAuthenticated }): React.ReactNode} props.children
 *
 * @example
 *   <AppShell appName="secmaster" title="Sec Master" api={api}>
 *     {({ userInfo }) => <Dashboard userId={userInfo?.userDetails} />}
 *   </AppShell>
 */
export const AppShell = ({
  appName,
  title,
  api,
  permissionPrefix,
  themeOptions,
  contentSx,
  headerContent,
  children,
}) => {
  requireProp(appName, 'appName');
  requireProp(title, 'title');
  requireProp(api, 'api');

  // Only pass layout props the app set, so AuthLayout keeps its own defaults.
  const layoutProps = {};
  if (contentSx !== undefined) layoutProps.contentSx = contentSx;
  if (headerContent !== undefined) layoutProps.headerContent = headerContent;

  return (
    <DarkModeProvider appName={appName} themeOptions={themeOptions}>
      <NotificationProvider>
        <PermissionCenterProvider
          api={api}
          appName={appName}
          permissionPrefix={permissionPrefix || appName}
        >
          <AuthLayout title={title} {...layoutProps}>
            {children}
          </AuthLayout>
        </PermissionCenterProvider>
      </NotificationProvider>
    </DarkModeProvider>
  );
};

export default AppShell;
